import React from 'react'

import Image from '../../../../../components/pdf/Image'
import { List, ListItem } from '../../../../../components/pdf/List'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Bullets, Paragraph, SectionHeading, SubHeading, SubSubHeading } from './_shared/Prose'

/**
 * Seção 4 - Direitos e Deveres – Responsabilidades Legais em Segurança e Saúde no
 * Trabalho. Texto literal do modelo aprovado por Julia Buzzo ("Modelo PGR 2026 - Banco
 * Bradesco_Varejo"). Substitui o placeholder institucional anterior (arquivo era
 * `04.MetodologiaSection.js` — o conteúdo real de Metodologia é a Seção 5, ver
 * `05.MetodologiaSection.js`).
 *
 * Print da plataforma "integrarh" (trilha de treinamento obrigatória sobre assédio) logo
 * antes de "4.2. Responsabilidades do Trabalhador": ausente na versão anterior — Julia
 * reportou a falta em revisão visual comparando com o PDF-modelo real. Imagem extraída
 * limpa (sem a marca d'água/overlay do arquivo de exemplo) direto do PDF-modelo e salva em
 * `public/pdfs/bradesco/treinamento-assedio-integrarh.png`.
 */
const DireitosEDeveresSection = () => (
  <View id="4">
    <Separator weight={15} />
    <SectionHeading numero={4}>DIREITOS E DEVERES – RESPONSABILIDADES LEGAIS EM SEGURANÇA E SAÚDE NO TRABALHO</SectionHeading>

    <Paragraph>
      De acordo com o disposto na NR-1 – Disposições Gerais, é responsabilidade do empregador e dos trabalhadores a
      observância das normas regulamentadoras de segurança e saúde no trabalho, conforme descrito a seguir:
    </Paragraph>

    <SubHeading id="4.1">4.1. Responsabilidades do Empregador</SubHeading>
    <Paragraph>
      Cabe ao Banco Bradesco S.A., na condição de empregador, assegurar o cumprimento das normas legais e
      regulamentares, por meio das seguintes ações:
    </Paragraph>

    <List>
      <ListItem>
        <Text size="p" align="justify">
          a) cumprir e fazer cumprir as disposições legais e regulamentares sobre segurança e saúde no trabalho;
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          b) informar aos trabalhadores:
        </Text>
        <List>
          <ListItem>
            <Text size="p" align="justify">
              I. os riscos ocupacionais existentes nos locais de trabalho (ISSO - Instruções de Segurança e Saúde
              Ocupacional, disponibilizado no Portal Corporativo);
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              II. as medidas de prevenção adotadas pela empresa para eliminar ou reduzir tais riscos;
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              III. os resultados dos exames médicos e de exames complementares de diagnóstico aos quais os próprios
              trabalhadores forem submetidos; e
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              IV. os resultados das avaliações ambientais realizadas nos locais de trabalho.
            </Text>
          </ListItem>
        </List>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          c) elaborar ordens de serviço sobre segurança e saúde no trabalho, dando ciência aos trabalhadores. O
          procedimento atual do Banco Bradesco S.A é realizado através das ISSO – Instrução de Segurança e Saúde
          Ocupacional.
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          d) permitir que representantes dos trabalhadores acompanhem a fiscalização dos preceitos legais e
          regulamentares sobre segurança e saúde no trabalho;
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          e) determinar procedimentos que devem ser adotados em caso de acidente ou doença relacionada ao trabalho,
          incluindo a análise de suas causas;
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          f) disponibilizar à Inspeção do Trabalho todas as informações relativas à segurança e saúde no trabalho; e
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          g) implementar medidas de prevenção, ouvidos os trabalhadores, de acordo com a seguinte ordem de
          prioridade:
        </Text>
        <List>
          <ListItem>
            <Text size="p" align="justify">
              I. eliminação dos fatores de risco;
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              II. minimização e controle dos fatores de risco, com a adoção de medidas de proteção coletiva;
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              III. minimização e controle dos fatores de risco, com a adoção de medidas administrativas ou de
              organização do trabalho; e
            </Text>
          </ListItem>
          <ListItem>
            <Text size="p" align="justify">
              IV. adoção de medidas de proteção individual.
            </Text>
          </ListItem>
        </List>
      </ListItem>
    </List>
    <Separator weight={6} />

    <SubSubHeading>4.1.1. Medidas contra o assédio e a violência no trabalho</SubSubHeading>

    <Paragraph>
      Com a publicação da Lei 14.457/22 e Portaria MTP nº 4.219/22, a CIPA - Comissão Interna de Prevenção de
      Acidentes e de Assédio - passa a ter um papel importante no conhecimento dos canais existentes na Organização,
      visando a prevenir e combater o assédio sexual e outras formas de violência no trabalho.
    </Paragraph>

    <Paragraph>
      O Banco Bradesco S.A. mantém o compromisso com a promoção de um ambiente de trabalho seguro, ético e
      respeitoso, pautado na dignidade e integridade de todos os funcionários. As diretrizes, procedimentos e
      recursos voltados à prevenção e ao enfrentamento do assédio e da violência laboral estão formalmente
      estabelecidos em seus normativos internos, amplamente divulgados e acessíveis por meio da intranet
      corporativa.
    </Paragraph>

    <Paragraph>
      Todos os funcionários têm acesso facilitado às informações e ferramentas necessárias para a consulta ao Código
      de Conduta, participação em programas de capacitação e utilização dos canais de ética, que garantem o
      acolhimento, a confidencialidade e o tratamento adequado das denúncias. Tais medidas reforçam o compromisso da
      organização com a conformidade legal e com a valorização das relações humanas no ambiente de trabalho.
    </Paragraph>

    <Paragraph>Canais de denúncias:</Paragraph>
    <Bullets
      items={[
        'Formulário eletrônico, disponível no Portal Corporativo, Site Bradesco RI >> Canal Corporativo de Denúncias - Bradesco RI - https://www.bradescori.com.br/governanca-corporativa/canal-corporativo-de-denuncias/',
        'Site Institucional Bradesco - https://banco.bradesco/html/classic/atendimento/denuncia/index.shtm',
        'Telefone: 0800 776 4820, de segunda a sexta-feira, das 08h às 18h, exceto fins de semana e feriados.'
      ]}
    />

    <Paragraph>
      No Portal Corporativo da Organização Bradesco (Intranet) está disponível uma trilha de treinamento obrigatória
      para todos os funcionários, abordando o tema assédio moral e sexual. O conteúdo é composto por sete episódios
      que tratam de definições, medidas de prevenção e combate ao assédio, causas e consequências, além dos canais
      de tratamento e denúncia.
    </Paragraph>

    <View style={{ display: 'flex', justifyContent: 'center' }}>
      <Image
        src="/pdfs/bradesco/treinamento-assedio-integrarh.png"
        maxWidth={430}
        ignoreFixedSize
        alt="Plataforma integrarh - Websérie Assédio! É da nossa conta!"
      />
    </View>
    <Separator weight={10} />

    <SubHeading id="4.2">4.2. Responsabilidades do Trabalhador</SubHeading>
    <List>
      <ListItem>
        <Text size="p" align="justify">
          a) cumprir as disposições legais e regulamentares sobre segurança e saúde no trabalho, inclusive as ordens
          de serviço expedidas pelo empregador;
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          b) submeter-se aos exames médicos e aos estudos organizacionais físicos, ambientais, psicossociais e
          outros previstos nas NR (conforme o item 1.5.3.3, alínea "b");
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          c) colaborar com a organização na aplicação das NR; e
        </Text>
      </ListItem>
      <ListItem>
        <Text size="p" align="justify">
          d) usar o equipamento de proteção individual fornecido pelo empregador, quando necessário e indicado pela
          área de técnica após estudo de risco.
        </Text>
      </ListItem>
    </List>
    <Separator weight={6} />

    <SubHeading id="4.3">4.3. Melhoria Contínua do Desempenho em SST</SubHeading>
    <Paragraph>
      Em conformidade com o item 1.5.3.4 da NR-1, o Banco Bradesco S.A. adota medidas sistemáticas e contínuas para
      avaliar e aprimorar o desempenho em Segurança e Saúde no Trabalho (SST), com foco na preservação da saúde,
      integridade física e bem-estar dos trabalhadores.
    </Paragraph>

    <Paragraph>As ações adotadas para atender a essa exigência incluem:</Paragraph>
    <Bullets
      items={[
        'Monitoramento contínuo dos indicadores de desempenho em SST, tais como número de incidentes, afastamentos, taxas de absenteísmo, adesão ao uso de EPIs, participação em treinamentos, eventos sentinela (para o caso dos fatores de Risco Psicossociais Relacionados ao Trabalho), entre outros;',
        'Realização periódica de auditorias internas e avaliações técnicas para verificar a conformidade legal e a eficácia das medidas de prevenção implantadas;',
        'Análise crítica dos resultados obtidos nas avaliações ambientais e nos exames ocupacionais, com geração de planos de ação corretivo e preventivo;',
        'Promoção de ações educativas e treinamentos recorrentes para todos os níveis hierárquicos, visando fortalecer a cultura de prevenção e o comportamento seguro;',
        'Revisão e atualização do PGR e demais programas de SST, sempre que identificadas não conformidades, mudanças no ambiente de trabalho, introdução de novas tecnologias ou recomendações decorrentes de investigações de incidentes;'
      ]}
    />

    <SubHeading id="4.4">4.4. Ações e Programas de Gerenciamento de Segurança, Saúde e Bem-Estar</SubHeading>

    <SubSubHeading>4.4.1. Programa Viva Bem</SubSubHeading>
    <Paragraph>
      O Programa Viva Bem é uma iniciativa estruturada que atua como estratégia complementar ao Gerenciamento de
      Riscos Ocupacionais (GRO), conforme previsto na NR-1, com foco no cuidado integral à saúde do trabalhador.
      Baseado em três pilares – Saudável, Movimento e Equilíbrio –, o programa reforça a gestão dos fatores de risco
      psicossociais, conforme as diretrizes internacionais (TR-ISO 10075 e ISO 45003) e contribui diretamente para a
      promoção de um ambiente de trabalho mais saudável, seguro e produtivo.
    </Paragraph>

    <Paragraph>Objetivos Estratégicos:</Paragraph>
    <Bullets
      items={[
        'Prevenir, tratar e reabilitar aspectos relacionados à saúde mental e bem-estar.',
        'Reduzir impactos ocupacionais associados ao estresse, absenteísmo, presenteísmo, adoecimentos mentais e desengajamento.',
        'Fortalecer a cultura organizacional centrada em saúde integral, cuidado humanizado e autocuidado.'
      ]}
    />

    <Paragraph>Estrutura Técnica do Programa:</Paragraph>
    <Bullets
      items={[
        'Pilar SAUDÁVEL: Foco em saúde mental, autoconhecimento, rede de apoio e gestão das emoções. Integra-se às trilhas de psicoeducação e ações preventivas, considerando os fatores de risco psicossociais mapeados nas análises ambientais e organizacionais.',
        'Pilar MOVIMENTO: Atividade física como vetor de transformação em saúde. Ações coletivas e individuais orientadas à melhoria do condicionamento físico, combate ao sedentarismo e promoção da vitalidade no ambiente corporativo.',
        'Pilar EQUILÍBRIO: Estímulo ao autocuidado, adoção de hábitos saudáveis e prevenção de doenças crônicas. Esse pilar contribui diretamente para a redução da probabilidade de adoecimento e, consequentemente, para a diminuição do risco na matriz do PGR.'
      ]}
    />

    <Paragraph>Destaques e Evolução Histórica:</Paragraph>
    <Bullets
      items={[
        'Canal Viva Bem 0800 – Atendimento gratuito, confidencial e 24h para funcionários, familiares, ex-funcionários e terceiros.',
        'Atenção à parentalidade atípica – Suporte especializado a famílias com filhos em condição de deficiência ou neurodivergência.',
        'Trilhas personalizadas por vertical de negócio – Adaptação do conteúdo e dos suportes conforme o perfil ocupacional e Setor (GHE).'
      ]}
    />

    <Paragraph>
      Contribuições para o PGR: o Programa Viva Bem atua como política e programa preventivo dentro do
      macroprocesso do GRO, auxiliando o Bradesco a:
    </Paragraph>
    <Bullets
      items={[
        'Mitigar os fatores de risco que impactam negativamente a saúde dos trabalhadores.',
        'Implementar ações de suporte e recuperação que atendem os critérios de reabilitação funcional e de retorno ao trabalho seguro.',
        'Gerar indicadores qualitativos e quantitativos que alimentam os registros de monitoramento contínuo do PGR.',
        'Promover a experiência do colaborador (Employee Experience) como eixo de saúde, desempenho e sustentabilidade do trabalho.'
      ]}
    />

    <Paragraph>
      Essas práticas visam não apenas a conformidade normativa, mas também a evolução contínua do desempenho
      organizacional em segurança e saúde, fortalecendo o compromisso institucional com um ambiente de trabalho
      seguro, saudável e sustentável.
    </Paragraph>

    <SubSubHeading>4.4.2. SIPAT</SubSubHeading>
    <Paragraph>
      A Semana Interna de Prevenção de Acidentes de Trabalho (SIPAT) do Banco Bradesco S.A. é uma ação fundamental
      no calendário da área de saúde e segurança, voltada à conscientização dos funcionários sobre práticas seguras
      e saudáveis no ambiente corporativo. Realizada anualmente, a SIPAT promove momentos de reflexão, aprendizado e
      engajamento, reforçando a importância da prevenção como ferramenta essencial para a construção de um ambiente
      de trabalho mais seguro, saudável e produtivo.
    </Paragraph>

    <Paragraph>
      A iniciativa reforça o compromisso organizacional com a valorização das pessoas e com a gestão responsável dos
      riscos ocupacionais, criando espaços de diálogo, aprendizado e integração. As ações buscaram estimular o
      autocuidado e fortalecer a cultura de prevenção.
    </Paragraph>

    <Paragraph>
      As ações da SIPAT contemplam também o formato digital, acessível a todos os funcionários do Banco Bradesco
      S.A., assegurando que a informação e a conscientização ultrapassem barreiras físicas e alcancem toda a
      organização. Essa iniciativa fortalece o engajamento dos colaboradores e reafirma nosso compromisso com a
      prevenção e a promoção do bem-estar coletivo.
    </Paragraph>

    <SubSubHeading>4.4.3. Comprometimento com a Segurança e Saúde: Reconhecimentos Institucionais</SubSubHeading>
    <Paragraph>
      O Banco Bradesco S.A. mantém um compromisso contínuo com a promoção da segurança, saúde e bem-estar dos seus
      funcionários. Esse comprometimento é refletido nas ações preventivas, programas de qualidade de vida e
      iniciativas voltadas à melhoria do ambiente de trabalho, que fazem parte da rotina do banco.
    </Paragraph>

    <Paragraph>
      Por meio da cultura Sou Bradesco, é fortalecido os pilares que definem a organização: a valorização da
      diversidade, o compromisso com o desenvolvimento, a criação de um ambiente de trabalho saudável e a
      construção de um espaço de confiança e reconhecimento.
    </Paragraph>

    <Paragraph>
      O cuidado com as pessoas está expresso na Política de Gerenciamento dos Recursos Humanos (Pessoas, Cultura &
      Performance) da Organização e tem sido reconhecido por diversos índices e prêmios, como por exemplo em 2024 a
      Certificação e Destaque Saúde Mental - Great People Mental Health e Jornada de Trabalho Flexível – GPTW.
    </Paragraph>

    <Paragraph>
      Essas práticas validam o compromisso do Banco na promoção da saúde e bem-estar de seus funcionários, alinhados
      às melhores práticas do setor. A cadeia estruturada de monitoramento e controles internos, isto é, mecanismos
      de acompanhamento de indicadores, identificação de riscos e ações preventivas, garantem continuamente o
      processo de melhoria contínua da cultura de segurança no ambiente de trabalho.
    </Paragraph>

    <Separator weight={15} />
  </View>
)

export default DireitosEDeveresSection
